import type { ReactElement, PropsWithChildren } from 'react';
import React, { useState, useMemo, useEffect } from 'react';
import type { AccountValidationType, AuthAccountWithPassType } from 'api-client/accounts';
import type { DiscoveredAccountDetails } from 'bc-model';
import { LoginProviderEnum } from 'bc-model';
import { includeBasePath } from 'utils/url-utils';

enum FormStepEnum {
  AuthenticateAndLogin = 'AuthenticateAndLogin',
  Error = 'Error',
  Forgot = 'Forgot',
  Login = 'Login',
  LoginUnavailable = 'LoginUnavailable',
  Register = 'Register',
  SSOAccountDiscovered = 'SSOAccountDiscovered',
  SSOChooseLinkOrRegister = 'SSOChooseLinkOrRegister',
  SSOLinkAccount = 'SSOLinkAccount',
  SSOLinkSuccess = 'SSOLinkSuccess',
  SSOVerifyExistingAccount = 'SSOVerifyExistingAccount',
}

type SmartLoginContextState = {
  formStep: FormStepEnum;
  profile: AuthAccountWithPassType;
  discoveredAccountDetails?: DiscoveredAccountDetails;
  provider: LoginProviderEnum;
  matchingId: string;
  queryString: string;
  formError?: string;
  name?: string;
  redirectLocationAfterLogin?: string;
  allowedSchemas: string[];
  showCuiDisclaimer: boolean;
  accountLinkingEnabled: boolean;
};

type SmartLoginContextActions = {
  setFormStep: (value: FormStepEnum) => void;
  goToPreviousStep: () => void;
  formStepHistory: FormStepEnum[];
  setUserProfileInfo: React.Dispatch<React.SetStateAction<AuthAccountWithPassType>>;
  setDiscoveredAccountDetails: React.Dispatch<React.SetStateAction<DiscoveredAccountDetails | undefined>>;
  setLoginProvider: (value: LoginProviderEnum) => void;
  setMatchingId: (value: string) => void;
  setQueryString: (value: string) => void;
  setFormError: (value: string) => void;
  setName: (value: string) => void;
  setRedirectLocationAfterLogin: (value: string) => void;
};

const SmartLoginStateContext = React.createContext({} as SmartLoginContextState);

const SmartLoginActionsContext = React.createContext({} as SmartLoginContextActions);

type LoginContextProvidedValuesProps = {
  withProfile?: AuthAccountWithPassType;
  authQueryString: string;
  allowedSchemas: LoginProviderEnum[];
  showCuiDisclaimer: boolean;
  accountValidation: AccountValidationType | undefined;
  accountLinkingEnabled: boolean;
};

type PropsType = PropsWithChildren<LoginContextProvidedValuesProps>;

function SmartLoginContextProvider({
  children,
  withProfile,
  authQueryString,
  allowedSchemas,
  showCuiDisclaimer,
  accountValidation,
  accountLinkingEnabled,
}: PropsType): ReactElement {
  const [formStepHistory, setFormStepHistory] = useState<FormStepEnum[]>([]);
  const [profile, setUserProfileInfo] = useState(withProfile ?? ({} as AuthAccountWithPassType));
  const [discoveredAccountDetails, setDiscoveredAccountDetails] = useState<
    DiscoveredAccountDetails | undefined
  >(undefined);
  const [provider, setLoginProvider] = useState(LoginProviderEnum.Local);
  const [matchingId, setMatchingId] = useState('');
  const [queryString, setQueryString] = useState(authQueryString);
  const [formError, setFormError] = useState<string | undefined>(undefined);
  const [name, setName] = useState<string | undefined>(undefined);
  const [redirectLocationAfterLogin, setRedirectLocationAfterLogin] = useState<string>(
    includeBasePath('/')
  );

  useEffect(() => {
    const loginFormAvailable = allowedSchemas.some(
      (element) =>
        element === LoginProviderEnum.ActiveDirectory || element === LoginProviderEnum.Local
    );

    let initFormStep = loginFormAvailable
      ? FormStepEnum.AuthenticateAndLogin
      : FormStepEnum.LoginUnavailable;

    if (accountValidation?.hasUnlinkedProfile) {
      initFormStep = accountLinkingEnabled
        ? FormStepEnum.SSOAccountDiscovered
        : FormStepEnum.Register;
    } else if (accountValidation?.mustRegister) {
      initFormStep = accountLinkingEnabled
        ? FormStepEnum.SSOChooseLinkOrRegister
        : FormStepEnum.Register;
    }

    setFormStepHistory([initFormStep]);

    if (accountValidation?.userDetails) {
      setUserProfileInfo(accountValidation.userDetails);
    }

    // When a matching account was found (hasUnlinkedProfile), ensure discoveredAccountDetails
    // is at least {} so MatchingAccountCard shows the Onebrief branding even if the account
    // has no rank/org/jobTitle details.
    setDiscoveredAccountDetails(
      accountValidation?.hasUnlinkedProfile
        ? (accountValidation.discoveredAccountDetails ?? {})
        : accountValidation?.discoveredAccountDetails
    );

    if (accountValidation?.loginProvider) {
      setLoginProvider(accountValidation.loginProvider);
    }

    if (accountValidation?.hasUnlinkedProfile) {
      if (accountValidation?.userDetails.email) {
        setMatchingId(accountValidation.userDetails.email);
      } else if (accountValidation?.userDetails.username) {
        setMatchingId(accountValidation.userDetails.username);
      }
    }
  }, [
    accountValidation?.hasUnlinkedProfile,
    accountValidation?.mustRegister,
    accountValidation?.userDetails,
    accountValidation?.discoveredAccountDetails,
    accountValidation?.loginProvider,
    allowedSchemas,
    accountLinkingEnabled,
  ]);

  const initialState: SmartLoginContextState = useMemo(
    () => ({
      formStep: formStepHistory[formStepHistory.length - 1],
      profile,
      discoveredAccountDetails,
      provider,
      matchingId,
      queryString,
      formError,
      name,
      redirectLocationAfterLogin,
      allowedSchemas,
      showCuiDisclaimer,
      accountLinkingEnabled,
    }),
    [
      formStepHistory,
      profile,
      discoveredAccountDetails,
      provider,
      matchingId,
      queryString,
      formError,
      name,
      redirectLocationAfterLogin,
      allowedSchemas,
      showCuiDisclaimer,
      accountLinkingEnabled,
    ]
  );

  const actions: SmartLoginContextActions = useMemo(() => {
    const setFormStep = (step: FormStepEnum) => {
      setFormStepHistory([...formStepHistory, step]);
    };
    const goToPreviousStep = () => {
      setFormStepHistory(formStepHistory.slice(0, -1));
    };
    return {
      formStepHistory,
      setFormStep,
      goToPreviousStep,
      setUserProfileInfo,
      setDiscoveredAccountDetails,
      setLoginProvider,
      setMatchingId,
      setQueryString,
      setFormError,
      setName,
      setRedirectLocationAfterLogin,
    };
  }, [
    setFormStepHistory,
    formStepHistory,
    setUserProfileInfo,
    setDiscoveredAccountDetails,
    setLoginProvider,
    setMatchingId,
    setQueryString,
    setFormError,
    setName,
    setRedirectLocationAfterLogin,
  ]);

  return (
    <SmartLoginStateContext.Provider value={initialState}>
      <SmartLoginActionsContext.Provider value={actions}>
        {children}
      </SmartLoginActionsContext.Provider>
    </SmartLoginStateContext.Provider>
  );
}

export default SmartLoginContextProvider;
export { SmartLoginStateContext, SmartLoginActionsContext, FormStepEnum };
